import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { useStore } from '../../contexts/StoreProvider';

const PaymentCallbackScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchOrders, orders } = useStore();
  
  const status = searchParams.get('status');
  const transactionId = searchParams.get('transactionId');
  const clientRefRaw = searchParams.get('order_number') || searchParams.get('clientReference') || localStorage.getItem('pending_hubtel_order');
  const orderNumber = clientRefRaw ? clientRefRaw.split('-retry-')[0] : null;

  // Clear it so it doesn't linger for future unrelated visits
  useEffect(() => {
    if (clientRefRaw && localStorage.getItem('pending_hubtel_order')) {
      localStorage.removeItem('pending_hubtel_order');
    }
  }, [clientRefRaw]);

  const [verificationStatus, setVerificationStatus] = useState('VERIFYING'); // VERIFYING, SUCCESS, FAILED, PENDING
  const [countdown, setCountdown] = useState(5);

  const isSuccessParams = status === 'Success' || status === 'Completed' || status?.toLowerCase() === 'success' || status?.toLowerCase() === 'paid';

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 6; // 30 seconds max
    const interval = 5000;
    let timerId = null;

    const checkStatus = async () => {
      try {
        if (!orderNumber) {
           setVerificationStatus(isSuccessParams ? 'SUCCESS' : 'FAILED');
           return;
        }

        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const res = await fetch(`${API_BASE}/api/store/payments/hubtel_status/?client_reference=${clientRefRaw}`);
        const data = await res.json();

        if (data.success && data.data && Array.isArray(data.data) && data.data.length > 0) {
          const txData = data.data[0];
          const txStatus = txData.TransactionStatus?.toString().toLowerCase();

          if (txStatus === 'success') {
            setVerificationStatus('SUCCESS');
            fetchOrders();
            return;
          } else if (txStatus === 'failed') {
            setVerificationStatus('FAILED');
            return;
          }
        }
        
        // Still pending or unknown response code
        attempts++;
        if (attempts >= maxAttempts) {
          setVerificationStatus('PENDING');
        } else {
          timerId = setTimeout(checkStatus, interval);
        }
      } catch (err) {
        attempts++;
        if (attempts >= maxAttempts) {
          setVerificationStatus('PENDING');
        } else {
          timerId = setTimeout(checkStatus, interval);
        }
      }
    };

    checkStatus();

    return () => {
      if (timerId) clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, isSuccessParams]);

  useEffect(() => {
    if (verificationStatus === 'SUCCESS') {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        navigate('/store/orders');
      }
    }
  }, [verificationStatus, countdown, navigate]);

  if (verificationStatus === 'VERIFYING') {
    return (
      <div className="flex flex-col items-center justify-center py-32 px-4 text-center">
        <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600 font-semibold uppercase tracking-wider">Verifying payment with Hubtel...</p>
        <p className="text-sm text-gray-400 mt-2">Please do not close this window.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
      {verificationStatus === 'SUCCESS' && <CheckCircle size={80} className="text-green-500 mb-6" />}
      {verificationStatus === 'FAILED' && <XCircle size={80} className="text-red-500 mb-6" />}
      {verificationStatus === 'PENDING' && <Clock size={80} className="text-yellow-500 mb-6" />}
      
      <h1 className="text-3xl font-bold uppercase tracking-widest mb-4">
        {verificationStatus === 'SUCCESS' && 'Payment Successful'}
        {verificationStatus === 'FAILED' && 'Payment Failed'}
        {verificationStatus === 'PENDING' && 'Payment Processing'}
      </h1>
      
      <p className="text-gray-600 mb-8 max-w-md">
        {verificationStatus === 'SUCCESS' && `Thank you for your purchase. Your order ${orderNumber ? `(${orderNumber})` : ''} has been confirmed and is being processed.`}
        {verificationStatus === 'FAILED' && 'Unfortunately, your payment could not be completed. Please check your payment details and try again.'}
        {verificationStatus === 'PENDING' && 'Your payment is taking longer than usual to confirm. We are processing it in the background. Please check your orders later to see the updated status.'}
      </p>

      {transactionId && (
        <p className="text-sm text-gray-400 mb-10">Transaction ID: {transactionId}</p>
      )}

      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
        <Link 
          to="/store/orders"
          className="flex-1 bg-black text-white px-6 py-4 font-bold tracking-widest uppercase hover:bg-gray-900 transition-colors text-center"
        >
          {verificationStatus === 'SUCCESS' ? `View Orders (${countdown}s)` : 'View Orders'}
        </Link>
        {verificationStatus !== 'SUCCESS' && (
          <Link 
            to="/store"
            className="flex-1 bg-white text-black border-2 border-black px-6 py-4 font-bold tracking-widest uppercase hover:bg-gray-50 transition-colors text-center"
          >
            Return to Store
          </Link>
        )}
      </div>
    </div>
  );
};

export default PaymentCallbackScreen;
